import { useMountEffect } from '@react-hookz/web';
import { eq, useLiveQuery } from '@tanstack/react-db';
import { MutationKey, useMutationState } from '@tanstack/react-query';
import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, Suspense, memo, useCallback, useEffect } from 'react';
import { z } from 'zod';

import { HOOFDSTUK_RESERVERING_COLLECTION_QUERY_KEY } from '@/api/collections/hoofdstuk-reservering/hoofdstuk-reservering';
import { useBesluitId } from '@/api/queries/besluit';
import { useGebruiker } from '@/api/queries/gebruiker/useGebruiker';
import {
  LinkParagraaf,
  LinkSubparagraaf,
  ensureQueryDataHoofdstukkenWithEdits,
  useHoofdstukHB,
  useOrderParagraafLink,
  useOrderSubparagraafLink,
  useOrderVoorschriftLink,
  useParagraafId,
  useSubparagraafId,
  useVoorschriftId,
} from '@/api/queries/objects';
import {
  ensureQueryDataHoofdstukByLineageId,
  useHoofdstukByLineageId,
} from '@/api/queries/objects/selectors/useHoofdstukByLineageId';
import { useGrondslagHasStandaardteksten } from '@/api/queries/std/selectors/useGrondslagHasStandaardteksten';
import { Divider } from '@/components/Divider/Divider';
import {
  DraggableEditor,
  DraggableParagraaf,
  DraggableSubparagraaf,
  DraggableVoorschrift,
  DroppableParagraaf,
  DroppableSubparagraaf,
  DroppableVoorschrift,
} from '@/components/DraggableEditor';
import { DropdownMenu } from '@/components/DropdownMenu/DropdownMenu';
import { ModalTrigger } from '@/components/Modal';
import { Warning } from '@/components/Warning/Warning';
import { EASING } from '@/constants/easing';
import { SolidSpinner } from '@/icons/components';
import {
  JumpToParagraafBase,
  JumpToParagraafFetch,
} from '@/modals/JumpToParagraaf';
import { useUserStore } from '@/stores/user';
import { useOffsetTop } from '@/utils/hooks/useOffsetTop';
import { twJoin } from '@/utils/tw-merge';

import { Hoofdstuk } from './-components/Hoofdstuk/Hoofdstuk';
import { HoofdstukAddBar } from './-components/Hoofdstuk/HoofdstukAddBar';
import {
  HoofdstukProvider,
  useHoofdstukContext,
} from './-components/Hoofdstuk/hoofdstukContext';
import { Paragraaf } from './-components/Paragraaf/Paragraaf';
import { ParagraafAddBar } from './-components/Paragraaf/ParagraafAddBar';
import { ParagraafSkeleton } from './-components/Paragraaf/ParagraafSkeleton';
import { SidebarRight } from './-components/SidebarRight/SidebarRight';
import { Slot } from './-components/Slot/Slot';
import { Subparagraaf } from './-components/Subparagraaf/Subparagraaf';
import { SubparagraafAddBar } from './-components/Subparagraaf/SubparagraafAddBar';
import { Toolbar } from './-components/Toolbar/Toolbar';
import { Voorschrift } from './-components/Voorschrift/Voorschrift';
import { VoorschriftAddBar } from './-components/Voorschrift/VoorschriftAddBar';
import { useEditorLinks } from './-hooks/useEditorLinks';
import {
  EDITOR_STORE_INITIAL_STATE,
  EditorView,
  SidebarLeftTab,
  useEditorStore,
} from './-store/useEditorStore';
import { getObjectState } from './-utils/getObjectState';
import { useAutoExtendReservering } from './-utils/useAutoExtendReservering';

const REORDERING_MUTATION_KEY: MutationKey = ['reordering'];

const searchSchema = z.object({
  recovered: z.boolean().optional(),
  focusParagraafId: z.string().optional(),
});

export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/voorschriften/$hoofdstukLineageId'
)({
  validateSearch: searchSchema.parse,
  loader: async ({
    context: { queryClient },
    params: { hoofdstukLineageId, besluitId },
  }) => {
    const [hoofdstuk] = await Promise.all([
      ensureQueryDataHoofdstukByLineageId(hoofdstukLineageId, queryClient),
      // Preload the users, these are used in the sidebar
      queryClient.ensureQueryData(useGebruiker.getOptions('')),
    ]);

    if (!hoofdstuk?.ID) return;

    const [links] = await Promise.all([
      queryClient.ensureQueryData(
        useEditorLinks.getOptions({
          hoofdstukId: hoofdstuk.ID,
          besluitId,
          view: EDITOR_STORE_INITIAL_STATE.view,
        })
      ),
      ensureQueryDataHoofdstukkenWithEdits({ besluitId }, {}, queryClient),
      queryClient.ensureQueryData(
        useHoofdstukHB.getOptions({ hoofdstukId: hoofdstuk.ID, besluitId })
      ),
      useBesluitId.ensureQueryData(besluitId),
    ]);

    /* Preload first subparagraaf */
    const firstSubparagraaf = links[0]?.subparagrafen[0];
    if (firstSubparagraaf) {
      await Promise.all([
        useParagraafId.ensureQueryData(
          firstSubparagraaf.paragraaf.PHB.Paragraaf
        ),
        useSubparagraafId.ensureQueryData(firstSubparagraaf.SPPB.Subparagraaf),
        ...firstSubparagraaf.voorschriften.map((voorschrift) =>
          useVoorschriftId.ensureQueryData(voorschrift.VSPB.Voorschrift)
        ),
      ]);
    }
  },
  component: VoorschriftenHoofdstukComponent,
  onEnter: ({ context: { queryClient }, search }) => {
    useEditorStore.getState().setSidebarLeftTabId(SidebarLeftTab.INDEX);

    if (search.recovered) {
      useEditorStore.getState().setView(EditorView.RECOVER);
    }

    queryClient?.invalidateQueries({
      queryKey: HOOFDSTUK_RESERVERING_COLLECTION_QUERY_KEY,
    });
  },
  onStay: ({ context: { queryClient }, search }) => {
    useEditorStore.getState().setSidebarLeftTabId(SidebarLeftTab.INDEX);
    useEditorStore
      .getState()
      .setView(
        search.recovered ? EditorView.RECOVER : EDITOR_STORE_INITIAL_STATE.view
      );
    useEditorStore.getState().clearSelectedObject();

    queryClient?.invalidateQueries({
      queryKey: HOOFDSTUK_RESERVERING_COLLECTION_QUERY_KEY,
    });
  },
  onLeave: () => {
    useEditorStore.getState().setView(EDITOR_STORE_INITIAL_STATE.view);
    useEditorStore.getState().clearSelectedObject();
  },
});

function VoorschriftenHoofdstukComponent() {
  const { hoofdstukLineageId, besluitId } = Route.useParams();
  const search = Route.useSearch();
  const view = useEditorStore((state) => state.view);
  const setView = useEditorStore((state) => state.setView);
  const isLabelBeheerMode = useEditorStore((state) => state.isLabelBeheerMode);

  const hoofdstuk = useHoofdstukByLineageId(hoofdstukLineageId);

  const links = useEditorLinks({
    hoofdstukId: hoofdstuk.data?.ID ?? '',
    besluitId,
    view,
  });

  /**
   * Check if user has reservering
   */
  const { hoofdstukReserveringCollection } = useRouteContext({
    from: '/$bedrijfLineageId/editor/$besluitId',
  });
  const hoofdstukReservering = useLiveQuery(
    (q) =>
      q
        .from({ hoofdstukReservering: hoofdstukReserveringCollection })
        .where(({ hoofdstukReservering }) =>
          eq(hoofdstukReservering.Reservering_lineage, hoofdstukLineageId)
        )
        .findOne(),
    [hoofdstukLineageId]
  );
  const userId = useUserStore((state) => state.user?.ID);

  const userHasReservering = hoofdstukReservering.data?.Gebruiker === userId;

  /**
   * Check if the current besluit is an initieel besluit
   */
  const besluit = useBesluitId(besluitId);

  /**
   * Check if current hoofdstuk is locked
   */
  const hb = useHoofdstukHB({
    hoofdstukId: hoofdstuk.data?.ID ?? '',
    besluitId,
  });

  /**
   * Check if the current grondslag has standaardteksten
   */
  const { data: grondslagHasStandaardteksten } =
    useGrondslagHasStandaardteksten(hoofdstuk.data?.Grondslag);

  /**
   * Auto extend hoofdstuk reservering
   */
  useAutoExtendReservering(hoofdstukLineageId);

  /**
   * If the besluit is bevroren, set the view to default
   */
  useEffect(() => {
    if (besluit.data?.Status === 'Bevroren') {
      setView(EditorView.DEFAULT);
    }
  }, [besluit.data?.Status, setView]);

  /**
   * Virtualizer
   */
  const { ref: listRef, offsetTop } = useOffsetTop();
  const virtualizer = useWindowVirtualizer({
    count: links.data?.length ?? 0,
    estimateSize: () => 1000,
    overscan: 1,
    initialOffset: () => 2,
    scrollMargin: offsetTop,
    paddingEnd: 300,
  });

  /**
   * When the focusParagraafId is set in the search params, scroll to the paragraaf on mount
   */
  useMountEffect(() => {
    if (!search.focusParagraafId) return;

    const initialOffset = links.data?.findIndex(
      (link) => link.PHB.Paragraaf === search.focusParagraafId
    );

    if (!initialOffset) return;

    virtualizer.scrollToIndex(initialOffset, { align: 'start' });
  });

  /**
   * Drag handlers for paragraaf
   */
  const orderParagraafLink = useOrderParagraafLink({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  const scrollToIndex = virtualizer.scrollToIndex;
  const onScrollToIndex = useCallback(
    ({ index }: { index: number }) => {
      scrollToIndex(index, { align: 'start' });
    },
    [scrollToIndex]
  );

  if (!links.data || !hoofdstuk.data || !hb.data || !besluit.data) return null;

  const onDropParagraaf = ({
    from,
    to,
    method,
  }: {
    from: number;
    to: number;
    method: 'prepend' | 'append';
  }) => {
    orderParagraafLink.mutate({
      from,
      to,
      method,
      links: links.data,
    });
  };

  const hoofdstukState = getObjectState(hoofdstuk.data);
  const isHoofdstukNew = hoofdstukState === 'new';

  const virtualItems = virtualizer.getVirtualItems();

  if (hoofdstuk.data.ObjectStatus === 'IngetrokkenGepubliceerd') {
    return (
      <div className="container-default relative mx-auto min-h-screen px-4">
        <Warning size="large" variant="warning" className="mt-[10rem]">
          <Warning.Title>
            {hoofdstuk.data.Nummer}.{' '}
            <span dangerouslySetInnerHTML={{ __html: hoofdstuk.data.Titel }} />
          </Warning.Title>

          <Warning.Description>
            Dit hoofdstuk is ingetrokken en gepubliceerd. Open het menu om een
            ander hoofdstuk te bekijken.
          </Warning.Description>
        </Warning>
      </div>
    );
  }

  const hoofdstukNummer = hoofdstuk.data.Nummer;

  return (
    <HoofdstukProvider
      hoofdstuk={hoofdstuk.data}
      hb={hb.data}
      userHasReservering={userHasReservering}
      besluit={besluit.data}
      grondslagHasStandaardteksten={!!grondslagHasStandaardteksten}
      links={links.data}
      hoofdstukState={hoofdstukState}
    >
      <div
        className={twJoin(
          'container-default relative mx-auto min-h-screen px-4 pt-4',
          isLabelBeheerMode && 'mt-6'
        )}
      >
        <ReorderingOverlay />

        <VoorschriftenToolbar
          links={links.data}
          hoofdstukNummer={hoofdstukNummer}
          besluitTypeId={besluit.data.Type}
          onScrollToIndex={onScrollToIndex}
        />

        <DraggableEditor allowReparenting={isHoofdstukNew}>
          <div
            className="relative w-full"
            style={{
              height: `${virtualizer.getTotalSize()}px`,
            }}
            ref={listRef}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                paddingTop: virtualItems.length ? 0 : `${offsetTop}px`,
                transform: `translateY(${(virtualItems[0]?.start ?? 0) - virtualizer.options.scrollMargin}px)`,
              }}
            >
              <Hoofdstuk isSelectable={!isLabelBeheerMode} />

              {!links.data.length && view === EditorView.ADD && (
                <ParagraafAddBar className="mb-2" testId="add-bar-0" />
              )}

              {virtualizer.getVirtualItems().map((item) => {
                const paragraafLink = links.data[item.index]!;
                const testId = `paragraaf-${item.index}`;

                return (
                  <div key={item.key}>
                    {view === EditorView.DRAGNDROP && (
                      <DroppableParagraaf
                        index={item.index}
                        method="prepend"
                        onDrop={(from) => {
                          onDropParagraaf({
                            from: from.index,
                            to: item.index,
                            method: 'prepend',
                          });
                        }}
                      />
                    )}
                    {view === EditorView.ADD && (
                      <ParagraafAddBar
                        className="mb-2"
                        previous={links.data[item.index - 1]}
                        next={paragraafLink}
                        testId={`add-bar-${item.index}`}
                      />
                    )}

                    <DraggableParagraaf index={item.index}>
                      {({ dragHandleRef, dragPreviewRef }) => (
                        <Suspense
                          fallback={
                            <ParagraafSkeleton
                              ref={virtualizer.measureElement}
                              data-index={item.index}
                              link={paragraafLink}
                              testId={testId}
                              transparent={isLabelBeheerMode}
                            />
                          }
                        >
                          <Paragraaf
                            ref={virtualizer.measureElement}
                            data-index={item.index}
                            link={paragraafLink}
                            isSelectable={
                              view !== EditorView.DRAGNDROP &&
                              !isLabelBeheerMode
                            }
                            transparentBg={isLabelBeheerMode}
                            testId={testId}
                            dragHandleRef={dragHandleRef}
                            dragPreviewRef={dragPreviewRef}
                          >
                            <Subparagrafen
                              paragraafLink={paragraafLink}
                              paragraafIndex={item.index}
                            />
                          </Paragraaf>
                        </Suspense>
                      )}
                    </DraggableParagraaf>

                    {item.index === links.data.length - 1 &&
                      view === EditorView.DRAGNDROP && (
                        <DroppableParagraaf
                          method="append"
                          index={item.index}
                          onDrop={(from) => {
                            onDropParagraaf({
                              from: from.index,
                              to: item.index,
                              method: 'append',
                            });
                          }}
                        />
                      )}

                    {item.index === links.data.length - 1 &&
                      view === EditorView.ADD && (
                        <ParagraafAddBar
                          className="mb-2"
                          previous={links.data[links.data.length - 1]}
                          testId={`add-bar-${links.data.length}`}
                        />
                      )}
                  </div>
                );
              })}

              {view === EditorView.ADD && (
                <>
                  <Divider className="mb-2" />
                  <HoofdstukAddBar />
                </>
              )}
            </div>
          </div>
        </DraggableEditor>
      </div>

      {hb.data.Type === 'MBIO' && (
        <Slot
          type="hoofdstuk"
          state={hoofdstukState}
          id={hoofdstuk.data.ID ?? ''}
          messageClassName="mt-[10rem]"
          className="fixed inset-0"
        />
      )}

      <SidebarRight
        hoofdstuk={hoofdstuk.data}
        hb={hb.data}
        userHasReservering={userHasReservering}
      />
    </HoofdstukProvider>
  );
}

const Subparagrafen = ({
  paragraafLink,
  paragraafIndex,
}: {
  paragraafLink: LinkParagraaf;
  paragraafIndex: number;
}) => {
  const { links } = useHoofdstukContext();

  const view = useEditorStore((state) => state.view);
  const isLabelBeheerMode = useEditorStore((state) => state.isLabelBeheerMode);

  const orderSubparagraafLink = useOrderSubparagraafLink({
    mutationKey: REORDERING_MUTATION_KEY,
  });

  const onDropSubparagraaf = ({
    from,
    to,
    method,
  }: {
    from: [number, number];
    to: [number, number];
    method: 'prepend' | 'append';
  }) => {
    orderSubparagraafLink.mutate({
      from,
      to,
      method,
      links,
    });
  };

  if (!paragraafLink.subparagrafen.length && view === EditorView.ADD) {
    return (
      <SubparagraafAddBar
        paragraafLink={paragraafLink}
        testId={`add-bar-${paragraafIndex}-0`}
      />
    );
  }

  return (
    <div
      className={twJoin(
        'flex flex-col',
        view !== EditorView.DRAGNDROP && 'gap-2'
      )}
    >
      {paragraafLink.subparagrafen.map(
        (subparagraafLink, subparagraafIndex) => {
          const path: [number, number] = [paragraafIndex, subparagraafIndex];

          return (
            <Fragment key={`subparagraaf-${subparagraafLink.SPPB.ID}`}>
              <DraggableSubparagraaf path={path}>
                {({ dragHandleRef, dragPreviewRef }) => (
                  <Subparagraaf
                    key={`subparagraaf-${subparagraafLink.SPPB.ID}`}
                    link={subparagraafLink}
                    testId={`subparagraaf-${paragraafIndex}-${subparagraafIndex}`}
                    dragHandleRef={dragHandleRef}
                    dragPreviewRef={dragPreviewRef}
                    isSelectable={
                      view !== EditorView.DRAGNDROP && !isLabelBeheerMode
                    }
                    transparentBg={isLabelBeheerMode}
                  >
                    <Voorschriften
                      subparagraafIndex={subparagraafIndex}
                      paragraafIndex={paragraafIndex}
                      subparagraafLink={subparagraafLink}
                    />
                  </Subparagraaf>
                )}
              </DraggableSubparagraaf>

              {view === EditorView.DRAGNDROP && (
                <DroppableSubparagraaf
                  method="append"
                  path={path}
                  onDrop={(from) => {
                    onDropSubparagraaf({
                      from,
                      to: path,
                      method: 'append',
                    });
                  }}
                />
              )}

              {view === EditorView.ADD && (
                <SubparagraafAddBar
                  paragraafLink={paragraafLink}
                  previous={subparagraafLink}
                  next={paragraafLink.subparagrafen[subparagraafIndex + 1]}
                  testId={`add-bar-${paragraafIndex}-${subparagraafIndex}`}
                />
              )}
            </Fragment>
          );
        }
      )}
    </div>
  );
};

const Voorschriften = memo(
  ({
    subparagraafLink,
    paragraafIndex,
    subparagraafIndex,
  }: {
    subparagraafLink: LinkSubparagraaf;
    paragraafIndex: number;
    subparagraafIndex: number;
  }) => {
    const { links, hoofdstukState } = useHoofdstukContext();
    const orderVoorschriftLink = useOrderVoorschriftLink({
      mutationKey: REORDERING_MUTATION_KEY,
    });

    const view = useEditorStore((state) => state.view);

    const onDropVoorschrift = ({
      from,
      to,
      method,
    }: {
      from: [number, number, number];
      to: [number, number, number];
      method: 'prepend' | 'append';
    }) => {
      orderVoorschriftLink.mutate({
        from,
        to,
        method,
        links,
      });
    };

    if (view === EditorView.ADD && !subparagraafLink.voorschriften.length) {
      return (
        <VoorschriftAddBar
          subparagraafLink={subparagraafLink}
          testId={`add-bar-${paragraafIndex}-${subparagraafIndex}-0`}
        />
      );
    }

    if (
      view === EditorView.DRAGNDROP &&
      !subparagraafLink.voorschriften.length &&
      hoofdstukState === 'new'
    ) {
      return (
        <DroppableVoorschrift
          path={[paragraafIndex, subparagraafIndex, 0]}
          method="prepend"
          onDrop={(from) => {
            onDropVoorschrift({
              from,
              to: [paragraafIndex, subparagraafIndex, 0],
              method: 'prepend',
            });
          }}
        />
      );
    }

    return (
      <div
        className={twJoin(
          'flex flex-col',
          view !== EditorView.DRAGNDROP && 'gap-1'
        )}
      >
        {subparagraafLink.voorschriften.map(
          (voorschriftLink, voorschriftIndex) => {
            return (
              <Fragment
                key={`${voorschriftLink.VSPB.Subparagraaf}-${voorschriftLink.VSPB.Voorschrift}`}
              >
                {view === EditorView.ADD && (
                  <VoorschriftAddBar
                    subparagraafLink={subparagraafLink}
                    previous={
                      subparagraafLink.voorschriften[voorschriftIndex - 1]
                    }
                    next={subparagraafLink.voorschriften[voorschriftIndex]}
                    testId={`add-bar-${paragraafIndex}-${subparagraafIndex}-${voorschriftIndex}`}
                  />
                )}

                {view === EditorView.DRAGNDROP && (
                  <DroppableVoorschrift
                    method="prepend"
                    path={[paragraafIndex, subparagraafIndex, voorschriftIndex]}
                    onDrop={(from) => {
                      onDropVoorschrift({
                        from,
                        to: [
                          paragraafIndex,
                          subparagraafIndex,
                          voorschriftIndex,
                        ],
                        method: 'prepend',
                      });
                    }}
                  />
                )}

                <DraggableVoorschrift
                  path={[paragraafIndex, subparagraafIndex, voorschriftIndex]}
                >
                  {({ dragHandleRef, dragPreviewRef }) => (
                    <Voorschrift
                      dragHandleRef={dragHandleRef}
                      dragPreviewRef={dragPreviewRef}
                      link={voorschriftLink}
                      isSelectable={view !== EditorView.DRAGNDROP}
                      testId={`voorschrift-${paragraafIndex}-${subparagraafIndex}-${voorschriftIndex}`}
                    />
                  )}
                </DraggableVoorschrift>
              </Fragment>
            );
          }
        )}

        {view === EditorView.ADD && (
          <VoorschriftAddBar
            subparagraafLink={subparagraafLink}
            previous={
              subparagraafLink.voorschriften[
                subparagraafLink.voorschriften.length - 1
              ]
            }
            testId={`add-bar-${paragraafIndex}-${subparagraafIndex}-${subparagraafLink.voorschriften.length}`}
          />
        )}

        {view === EditorView.DRAGNDROP && (
          <DroppableVoorschrift
            method="append"
            path={[
              paragraafIndex,
              subparagraafIndex,
              subparagraafLink.voorschriften.length - 1,
            ]}
            onDrop={(from) => {
              onDropVoorschrift({
                from,
                to: [
                  paragraafIndex,
                  subparagraafIndex,
                  subparagraafLink.voorschriften.length - 1,
                ],
                method: 'append',
              });
            }}
          />
        )}
      </div>
    );
  }
);

const ReorderingOverlay = () => {
  /**
   * Check if there's any autosave mutations going on.
   *
   * This is a workaround because useIsMutating breaks when adding standaardteksten voorschriften from different paragrafen.
   */
  const variables = useMutationState({
    filters: {
      mutationKey: REORDERING_MUTATION_KEY,
    },
    select: (mutation) => mutation.state.status === 'pending',
  });

  const isReordering = !!variables.find(Boolean);

  return (
    <AnimatePresence>
      {isReordering && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: EASING.cubicOut }}
          className="fixed inset-0 z-navigation-below flex items-center justify-center bg-gray-600/50"
        >
          <div className="flex size-5 items-center justify-center rounded-sm bg-white shadow-lg">
            <SolidSpinner className="animate-spin" size={17} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const VoorschriftenToolbar = memo(
  ({
    links,
    hoofdstukNummer,
    besluitTypeId,
    onScrollToIndex,
  }: {
    links: LinkParagraaf[];
    hoofdstukNummer: number;
    besluitTypeId: string;
    onScrollToIndex: JumpToParagraafBase['onClick'];
  }) => {
    return (
      <Toolbar
        optionsMenuContent={
          <ModalTrigger
            modalComponent={JumpToParagraafFetch}
            onOpen={() => ({
              onClick: onScrollToIndex,
              paragraafIds: links.map((link) => link.PHB.Paragraaf),
              hoofdstukNummer,
              stateIconOptions: {
                besluitTypeId,
              },
            })}
            asChild
          >
            <DropdownMenu.Button disabled={links.length === 0}>
              Alle paragrafen
            </DropdownMenu.Button>
          </ModalTrigger>
        }
      />
    );
  }
);
